import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';

import ActionCard, { ActionCards } from '../../../../../controls/common/actionCard';
import { GoPlay } from 'react-icons/go';

import { applyCellProps } from '../../domain/model';

/**
 * CellActions — what you can do to a cell, given the state it is in.
 *
 * Extracted for the reason CellDetailsView and CellWorkflowView were: the mission map's dock and the
 * cell's own screen are one click apart and read as one product, so an action offered on one and
 * missing from the other is a surface that disagrees with itself about what is possible. The cell
 * screen had no actions at all — a person who opened a cell to read its question had to go back to
 * the dock to answer it.
 *
 * Every card here CHANGES the cell. There used to be one that did not — an Answer/Continue button
 * that only opened the cell's own screen — and it is gone: both surfaces already navigate (the
 * dock's panel header has an Open cell control, and the cell's screen is where it led), so among
 * actions that do something it read as a fourth thing you might have to do.
 *
 * `canEdit` (default true) removes the section outright. Every card writes, so there is nothing of
 * it left to show read-only — unlike Details or Cell Workflow, which have a frozen view to fall
 * back on.
 */
// ---------------------------------------------------------------------------
// ActionZone — the "Actions" section: one compact card per applicable action
// ---------------------------------------------------------------------------

// ActionZone renders the whole "Actions" section (heading + hr + cards) for the cell's current
// status, or null when the status has no applicable action — so the section is hidden entirely,
// heading included. Each action is its own compact card.
//
// **Only the verbs that need no input get a button here.** Everything that carries a message —
// resuming a completed cell, steering a working one — is composed on the meta-thread, where the
// message is written into the record that carries it (06 §6.5).
//
// One button writes a status (`activate`, from a draft the heartbeat cannot see); every other one
// posts an INTENT and lets the orchestrator decide what the verb means.
//
// **The local reflection is done HERE, not handed back to a caller.** It used to be two optional
// callbacks, and the surface that forgot one had a button that wrote the status and then went on
// offering to write it — silently, because an absent callback is indistinguishable from one that
// chose to do nothing. `onApplyLocal` is a prop both surfaces already hold for every other write,
// so there is nothing left to forget.
export default function CellActions({ cell, canEdit, missionId, client, onActionDone,
                                     onApplyLocal, composer }) {
  const props = cell.props || {};
  const status = props.status || 'draft';
  // Can this cell be ASKED anything yet? Only `activate` works without a meta-thread, and only
  // because a draft has no transitions that need one. Every other verb is a record on a thread the
  // orchestrator creates on the cell's first reconcile — so a cell activated since the last tick
  // offers nothing, which is the truthful state of it, rather than a control that would fail.
  const askable = !client || !client.canIntend || client.canIntend(missionId, cell.element_id);
  // A verb is offered ONCE per surface, in the richest control that surface has. Where a composer
  // is present — the cell's own screen, at the foot of its meta-thread — the resting-state verbs
  // belong to it, because there they can carry the reason with them. The dock has no composer, so
  // they stay cards there: what it can offer is the verb without the message, which is the whole
  // of what a card is (06 §6.5).
  const restingVerbsHere = !composer;
  const [busy, setBusy] = React.useState(false);
  const [actionError, setActionError] = React.useState(null);

  const runAsync = async (fn) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      if (onActionDone) onActionDone();
    } catch (e) {
      setActionError(e && e.message ? e.message : 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  // ACTIVATE is the one transition this surface still writes as a status. A draft is invisible to
  // the heartbeat, so there is no second writer to race — and it has no meta-thread yet for an
  // intent to land on (06 §6.4).
  const activate = () => runAsync(async () => {
    await client.activateCell(missionId, cell.element_id);
    applyCellProps(onApplyLocal, missionId, cell, { status: 'planned' });
  });

  // Everything else asks. The record is the request and the orchestrator decides what the verb means
  // for the status the cell is in; nothing here writes `status`, and nothing here paints the status
  // it hopes for — what is reflected is that the cell HAS a pending request, which is the only
  // claim this surface is entitled to make and the only one it never has to retract when a verb is
  // refused.
  const ask = (verb) => () => runAsync(async () => {
    await client.postIntent(missionId, cell.element_id, verb);
    applyCellProps(onApplyLocal, missionId, cell, { intent_pending: true });
  });

  const pause = ask('pause');
  const resume = ask('resume');
  const restart = ask('restart');

  // Build the applicable action card(s) for this status. An empty list ⇒ the whole section is hidden.
  let cards = [];

  if (status === 'draft') {
    cards = [
      <ActionCard key="activate" helper="Activates the cell once dependencies are wired">
        <Button variant="success" size="sm" disabled={busy} onClick={activate}>
          <GoPlay /> {busy ? 'Activating…' : 'Activate Cell'}
        </Button>
      </ActionCard>,
    ];
  } else if (status === 'failed' && restingVerbsHere) {
    // Two ways out, and what separates them is the agent's MEMORY. Resume revives the run that
    // failed — the agent still holds the error and everything it concluded on the way, so "carry on"
    // is a complete instruction. Restart opens a new run of the same step with a fresh session,
    // which is the only way out of a context an agent has talked itself into.
    cards = [
      <ActionCard key="resume" helper="Picks the failed run back up, with everything the agent knew">
        <Button variant="success" size="sm" disabled={busy} onClick={resume}>
          {busy ? 'Resuming…' : 'Resume'}
        </Button>
      </ActionCard>,
      <ActionCard key="restart" helper="Runs the step again from scratch, with a fresh agent">
        <Button variant="light" size="sm" disabled={busy} onClick={restart}>
          {busy ? 'Restarting…' : 'Restart'}
        </Button>
      </ActionCard>,
    ];
  } else if (status === 'input_needed') {
    // Nothing here. A waiting run is continued through its own SESSION and nowhere else — the
    // answer goes to the agent that is on it, and there is no restarting away from a run whose
    // agent is still holding everything it knew (06 §6.3).
    // The question is answered where it was asked — on its own record in the cell's timeline, which
    // is the one place that knows whether the request is still open and which channel it takes.
    //
    // This panel used to carry a second answer UI, and it was wrong twice over. It rendered each
    // option as `{o}` against a request whose options are `{label}` objects, so a real request from
    // the orchestrator crashed the widget. And its submit posted an UNTAGGED text item to the
    // meta-thread, while the orchestrator resolves a gate only from an `input_provided` record
    // carrying `step`, `run` and a `label` — so the answers that did not crash were ignored, and the
    // cell sat waiting on a click that had already happened.
    //
    // Answering an in-flight request works now, and this button still opens the cell rather than
    // growing controls of its own. What an answer control has to know has only gone up: which of
    // two channels this request takes, whether it is still the cell's open one, and — on an
    // approval — that it must offer no free-text path, because the worker reads anything outside
    // seven affirmative words as a Deny. A second implementation of those rules 400px wide, beside
    // a graph, with no timeline to read the question in, is how the first one came to be wrong.
    // What waits here is a QUESTION, and only a question. A run whose agent DIED used to wait here
    // too, on the grounds that its harness session survived and was worth continuing — which left a
    // board saying `input_needed` about a run nobody had been asked anything about, and left the one
    // case that most wants a fresh agent able to do nothing but continue. Every failure reaches
    // `failed` now, where Resume and Restart both live and both carry a message (06 §6.3).
    //
    // No card for "go and answer it" either. It was navigation and nothing else, and both surfaces
    // already navigate: the dock's panel header has an Open cell control (and double-clicking the
    // node does the same), while on the cell's own screen the timeline holding the question is the
    // pane already open. An action that only takes you somewhere you can already get to, sitting
    // among actions that CHANGE the cell, reads as a fourth thing you might have to do.
    cards = [];
  } else if (status === 'planned' || status === 'working') {
    cards = [
      <ActionCard key="pause" helper="Stops the heartbeat from advancing this cell">
        <Button variant="warning" size="sm" disabled={busy} onClick={pause}>
          {busy ? 'Pausing…' : 'Pause'}
        </Button>
      </ActionCard>,
    ];
  } else if (status === 'inactive' && restingVerbsHere) {
    cards = [
      <ActionCard key="resume" helper="Resumes the cell from where it paused">
        <Button variant="success" size="sm" disabled={busy} onClick={resume}>
          {busy ? 'Resuming…' : 'Resume'}
        </Button>
      </ActionCard>,
    ];
  }

  // Every card here writes — activate, pause, resume, retry, restart, answer — so a read-only
  // revision or session takes the whole section with it, exactly as a status with nothing to do
  // does. There is no read-only half of "what you can do".
  if (cards.length === 0 || canEdit === false) return null;
  // `activate` is the only card that needs no thread, so a cell that cannot be asked yet shows the
  // section only when that is the card it has.
  if (!askable && status !== 'draft') return null;

  return (
    <>
      {/* A SECTION, the same one every Actions block in the app is — `Card.Text as="h6"` with no
          `hr`, because ActionCards' 1rem top margin IS that hr (see actionCard.js). It is not a
          subsection eyebrow: the sections above it describe what the cell IS, and this is the app's
          one place for what you can DO, which is why the playground, the debugger and the scheduler
          all head it the same way.

          `mt-3` is the fix for what brought us here: a Bootstrap heading has no top margin, so this
          sat directly on whatever the section above it ended with — 0px, measured. Adjacent margins
          collapse, so it reads as 16px after a paragraph's 1rem too rather than 32. Owning the space
          above it here means it separates from ANY preceding content, instead of depending on what
          that content happens to end with. */}
      <Card.Text as="h6" className="mt-3">Actions</Card.Text>
      <ActionCards>{cards}</ActionCards>
      {actionError && (
        <Alert variant="warning" style={{ fontSize: '0.8rem', marginBottom: '1rem' }}>{actionError}</Alert>
      )}
    </>
  );
}
